import { useState } from 'react';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { useTeacherPayroll, TeacherPayrollRecord } from '@/hooks/useTeacherPayroll';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, FileSpreadsheet, Loader2, MoreHorizontal, Wallet, UserCog, FileText } from 'lucide-react';
import { DepositManagementDialog } from '@/components/admin/DepositManagementDialog';
import { TeacherInfoEditDialog } from '@/components/admin/TeacherInfoEditDialog';
import { PayrollApprovalStatus } from '@/components/admin/payroll/PayrollApprovalStatus';
import { GenerateSlipDialog } from '@/components/admin/payroll/GenerateSlipDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import * as XLSX from 'xlsx';

interface SelectedTeacher {
  teacherId: string;
  teacherName: string;
  currentRefund: number;
  currentDeduction: number;
}

interface SelectedTeacherInfo {
  teacherCode: string;
  teacherName: string;
  currentBankAccount: string;
  currentBank: string;
  currentUid: string;
  currentAddress: string;
  currentIsTaxPnd3: boolean;
}

const AdminTeacherPayrollPage = () => {
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<SelectedTeacher | null>(null);
  const [teacherInfoDialogOpen, setTeacherInfoDialogOpen] = useState(false);
  const [selectedTeacherInfo, setSelectedTeacherInfo] = useState<SelectedTeacherInfo | null>(null);
  const [slipDialogOpen, setSlipDialogOpen] = useState(false);
  const [selectedSlipTeacher, setSelectedSlipTeacher] = useState<TeacherPayrollRecord | null>(null);
  
  const { data: payrollData, isLoading } = useTeacherPayroll(selectedMonth, selectedYear);

  const handleExportExcel = () => {
    if (!payrollData || payrollData.length === 0) return;

    // Remove hidden columns (month, year) for export
    const exportData = payrollData.map(({ month, year, ...rest }) => rest);

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Teacher Payroll');
    
    XLSX.writeFile(workbook, `teacher_payroll_${selectedMonth}_${selectedYear}.xlsx`);
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleOpenDepositDialog = (record: TeacherPayrollRecord) => {
    setSelectedTeacher({
      teacherId: record.รหัส,
      teacherName: record['ชื่อจริง-นามสกุล'] || record.ชื่อเล่น || record.รหัส,
      currentRefund: record.คืนเงินประกัน || 0,
      currentDeduction: record.หักเงินประกัน || 0,
    });
    setDepositDialogOpen(true);
  };

  const handleOpenTeacherInfoDialog = (record: TeacherPayrollRecord) => {
    setSelectedTeacherInfo({
      teacherCode: record.รหัส,
      teacherName: record['ชื่อจริง-นามสกุล'] || record.ชื่อเล่น || record.รหัส,
      currentBankAccount: record.เลขบัญชี || '',
      currentBank: record.ธนาคาร || '',
      currentUid: record.รหัสบัตรประชาชน || '',
      currentAddress: record.ที่อยู่ผู้เสียภาษี || '',
      currentIsTaxPnd3: record.is_tax_pnd3 ?? true,
    });
    setTeacherInfoDialogOpen(true);
  };

  const handleOpenSlipDialog = (record: TeacherPayrollRecord) => {
    setSelectedSlipTeacher(record);
    setSlipDialogOpen(true);
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <SidebarInset>
          <div className="container mx-auto px-4 py-8">
            {/* Header with filters and export button */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-6 w-6" />
                  อนุมัติเงินเดือนครู
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 items-center flex-wrap">
                  {/* Month Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">เดือน:</span>
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(Number(v))}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="เดือน" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                          <SelectItem key={month} value={month.toString()}>
                            เดือน {month}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Year Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">ปี:</span>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(Number(v))}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="ปี" />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026].map((year) => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Export Button */}
                  <Button onClick={handleExportExcel} disabled={!payrollData || payrollData.length === 0}>
                    <Download className="h-4 w-4 mr-2" />
                    Export Excel
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Approval Status */}
            <PayrollApprovalStatus month={selectedMonth} year={selectedYear} />

            {/* Data Table */}
            <Card className="mt-6">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">รหัส</TableHead>
                        <TableHead className="whitespace-nowrap">ชื่อ-นามสกุล</TableHead>
                        <TableHead className="whitespace-nowrap">ชื่อเล่น</TableHead>
                        <TableHead className="whitespace-nowrap">เลขบัญชี</TableHead>
                        <TableHead className="whitespace-nowrap">ธนาคาร</TableHead>
                        <TableHead className="text-right whitespace-nowrap">เงินเดือน</TableHead>
                        <TableHead className="text-right whitespace-nowrap">ค่าคอมมิชชั่น</TableHead>
                        <TableHead className="text-right whitespace-nowrap">รวมเงินเดือน</TableHead>
                        <TableHead className="text-right whitespace-nowrap">ภงด.3</TableHead>
                        <TableHead className="text-right whitespace-nowrap">คืนเงินประกัน</TableHead>
                        <TableHead className="text-right whitespace-nowrap">หักเงินประกัน</TableHead>
                        <TableHead className="text-right whitespace-nowrap">เงินเดือนสุทธิ</TableHead>
                        <TableHead className="text-center whitespace-nowrap">จัดการ</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={13} className="text-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                            <span className="text-muted-foreground mt-2 block">กำลังโหลด...</span>
                          </TableCell>
                        </TableRow>
                      ) : payrollData && payrollData.length > 0 ? (
                        payrollData.map((record) => (
                          <TableRow key={record.รหัส}>
                            <TableCell className="font-mono">{record.รหัส}</TableCell>
                            <TableCell>{record['ชื่อจริง-นามสกุล']}</TableCell>
                            <TableCell>{record.ชื่อเล่น}</TableCell>
                            <TableCell className="font-mono">{record.เลขบัญชี?.replace(/'/g, '') || '-'}</TableCell>
                            <TableCell>{record.ธนาคาร}</TableCell>
                            <TableCell className="text-right font-mono">{formatNumber(record.เงินเดือน)}</TableCell>
                            <TableCell className="text-right font-mono">{formatNumber(record.ค่าคอมมิชชั่น)}</TableCell>
                            <TableCell className="text-right font-mono">{formatNumber(record.รวมเงินเดือน)}</TableCell>
                            <TableCell className="text-right font-mono">{formatNumber(record['ภงด.3'])}</TableCell>
                            <TableCell className="text-right font-mono text-green-600">{formatNumber(record.คืนเงินประกัน)}</TableCell>
                            <TableCell className="text-right font-mono text-destructive">{formatNumber(record.หักเงินประกัน)}</TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                              {formatNumber(record.เงินสุทธิรวมประกัน)}
                            </TableCell>
                            <TableCell className="text-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleOpenDepositDialog(record)}>
                                    <Wallet className="h-4 w-4 mr-2" />
                                    จัดการเงินประกัน
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenTeacherInfoDialog(record)}>
                                    <UserCog className="h-4 w-4 mr-2" />
                                    แก้ไขข้อมูลครู
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenSlipDialog(record)}>
                                    <FileText className="h-4 w-4 mr-2" />
                                    สร้าง/ส่งสลิป
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                            ไม่พบข้อมูลสำหรับเดือน {selectedMonth}/{selectedYear}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            {payrollData && payrollData.length > 0 && (
              <Card className="mt-4">
                <CardContent className="pt-6">
                  <div className="flex justify-between text-sm">
                    <span>จำนวนครูทั้งหมด: <strong>{payrollData.length}</strong> คน</span>
                    <span>รวมเงินเดือนสุทธิ: <strong className="text-primary">
                      {payrollData.reduce((sum, r) => sum + (r.เงินสุทธิรวมประกัน || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong> บาท</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </SidebarInset>
      </div>

      {/* Deposit Management Dialog */}
      {selectedTeacher && (
        <DepositManagementDialog
          open={depositDialogOpen}
          onOpenChange={setDepositDialogOpen}
          teacherId={selectedTeacher.teacherId}
          teacherName={selectedTeacher.teacherName}
          month={selectedMonth}
          year={selectedYear}
          currentRefund={selectedTeacher.currentRefund}
          currentDeduction={selectedTeacher.currentDeduction}
        />
      )}

      {/* Teacher Info Edit Dialog */}
      {selectedTeacherInfo && (
        <TeacherInfoEditDialog
          open={teacherInfoDialogOpen}
          onOpenChange={setTeacherInfoDialogOpen}
          teacherCode={selectedTeacherInfo.teacherCode}
          teacherName={selectedTeacherInfo.teacherName}
          currentBankAccount={selectedTeacherInfo.currentBankAccount}
          currentBank={selectedTeacherInfo.currentBank}
          currentUid={selectedTeacherInfo.currentUid}
          currentAddress={selectedTeacherInfo.currentAddress}
          currentIsTaxPnd3={selectedTeacherInfo.currentIsTaxPnd3}
        />
      )}

      {/* Generate Slip Dialog */}
      {selectedSlipTeacher && (
        <GenerateSlipDialog
          open={slipDialogOpen}
          onOpenChange={setSlipDialogOpen}
          salary={selectedSlipTeacher}
        />
      )}
    </SidebarProvider>
  );
};

export default AdminTeacherPayrollPage;
